import type { Identifier } from "ra-core";

import type {
  ContactExpediente,
  ContactExpedienteBundle,
  ContactExpedienteInput,
  DealExpedienteMissingEntry,
  UploadContactExpedienteDocumentInput,
} from "../../types";
import {
  CONTACT_EXPEDIENTE_SIGNED_URL_EXPIRY_SECONDS,
  CONTACT_EXPEDIENTES_BUCKET,
  validateExpedienteDocumentFile,
} from "../commons/contactExpediente";
import { buildOperationDocumentStoragePath } from "../commons/operationDocuments";
import { computeSha256Hex } from "../commons/sha256";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EC2b (TASK-124): typed wrappers over the contact expediente RPCs of TASK-123. The
 * SECURITY DEFINER functions are the authority (the five-audience gate, validations, audit log)
 * and raise Spanish messages that are surfaced as-is. Files go to the PRIVATE
 * `contact-expedientes` bucket and are downloaded through a short-lived signed URL only, after the
 * audited path RPC. Nothing here logs a value: only the RPC name and the error object (which never
 * carries expediente values).
 */
const fail = (name: string, error: { message?: string }): never => {
  console.error(`${name}.error`, { message: error.message });
  throw new Error(error.message || "No se pudo completar la acción");
};

export const createSupabaseContactExpedienteMethods = () => ({
  /** The expediente of a contact (null when none yet) and its documents (audited read). */
  async getContactExpediente(
    contactId: Identifier,
  ): Promise<ContactExpedienteBundle> {
    const { data, error } = await getSupabaseClient().rpc(
      "get_contact_expediente",
      { p_contact_id: contactId },
    );
    if (error) fail("get_contact_expediente", error);
    return data as ContactExpedienteBundle;
  },

  /** Partial, audited save: only the keys present in `input` change. */
  async saveContactExpediente(
    contactId: Identifier,
    input: ContactExpedienteInput,
  ): Promise<ContactExpediente> {
    const { data, error } = await getSupabaseClient().rpc(
      "save_contact_expediente",
      { p_contact_id: contactId, p_data: input },
    );
    if (error) fail("save_contact_expediente", error);
    return data as ContactExpediente;
  },

  /**
   * Hashes the file (SHA-256), uploads it to the private `contact-expedientes` bucket under
   * `<contact_id>/<uuid>-<file_name>` (never overwriting an object) and registers the version.
   * Returns the new document id.
   */
  async uploadContactExpedienteDocument(
    input: UploadContactExpedienteDocumentInput,
  ): Promise<Identifier> {
    const { contactId, docType, file } = input;
    const invalid = validateExpedienteDocumentFile(file);
    if (invalid) throw new Error(invalid);

    const sha256 = await computeSha256Hex(file);
    const storagePath = buildOperationDocumentStoragePath(
      String(contactId),
      file.name,
    );
    const { error: uploadError } = await getSupabaseClient()
      .storage.from(CONTACT_EXPEDIENTES_BUCKET)
      .upload(storagePath, file, { upsert: false, contentType: file.type });
    if (uploadError)
      fail("uploadContactExpedienteDocument.upload", uploadError);

    // ASSUMPTION: if registering fails the uploaded object is orphaned (the bucket has no delete
    // policy by design); it is unreachable without a registered row.
    const { data, error } = await getSupabaseClient().rpc(
      "register_contact_expediente_document",
      {
        p_contact_id: contactId,
        p_doc_type: docType,
        p_storage_path: storagePath,
        p_file_name: file.name,
        p_mime_type: file.type,
        p_size_bytes: file.size,
        p_sha256: sha256,
      },
    );
    if (error) fail("register_contact_expediente_document", error);
    return data as Identifier;
  },

  /**
   * A short-lived signed URL for a document: the audited path RPC runs first (it logs the read),
   * never a public URL.
   */
  async getContactExpedienteDocumentUrl(
    documentId: Identifier,
  ): Promise<string> {
    const { data: path, error } = await getSupabaseClient().rpc(
      "get_contact_expediente_document_path",
      { p_document_id: documentId },
    );
    if (error) fail("get_contact_expediente_document_path", error);
    const { data, error: signError } = await getSupabaseClient()
      .storage.from(CONTACT_EXPEDIENTES_BUCKET)
      .createSignedUrl(
        path as string,
        CONTACT_EXPEDIENTE_SIGNED_URL_EXPIRY_SECONDS,
      );
    if (signError || !data) {
      console.error("getContactExpedienteDocumentUrl.error");
      throw new Error("No se pudo generar el enlace de descarga");
    }
    return data.signedUrl;
  },

  /** Soft delete (the reason is mandatory); the rows and files are kept. */
  async removeContactExpedienteDocument(
    documentId: Identifier,
    reason: string,
  ): Promise<void> {
    const { error } = await getSupabaseClient().rpc(
      "remove_contact_expediente_document",
      { p_document_id: documentId, p_reason: reason },
    );
    if (error) fail("remove_contact_expediente_document", error);
  },

  /** Labels (never values) of what is missing from a contact's expediente; any role that sees the contact. */
  async getContactExpedienteMissing(contactId: Identifier): Promise<string[]> {
    const { data, error } = await getSupabaseClient().rpc(
      "contact_expediente_missing",
      { p_contact_id: contactId },
    );
    if (error) fail("contact_expediente_missing", error);
    return (data ?? []) as string[];
  },

  /** The missing labels of every contact of a lead (for the closing guard). */
  async getDealExpedienteMissing(
    dealId: Identifier,
  ): Promise<DealExpedienteMissingEntry[]> {
    const { data, error } = await getSupabaseClient().rpc(
      "deal_expediente_missing",
      { p_deal_id: dealId },
    );
    if (error) fail("deal_expediente_missing", error);
    return (data ?? []) as DealExpedienteMissingEntry[];
  },

  /**
   * The missing labels of every contact of an operation's lead, for the sign dialog. Gated like
   * `sign_operation()` (so administracion is told what is missing too); labels only.
   */
  async getOperationExpedienteMissing(
    operationId: string,
  ): Promise<DealExpedienteMissingEntry[]> {
    const { data, error } = await getSupabaseClient().rpc(
      "operation_expediente_missing",
      { p_operation_id: operationId },
    );
    if (error) fail("operation_expediente_missing", error);
    return (data ?? []) as DealExpedienteMissingEntry[];
  },
});
